import { useState, useEffect } from "react";
import MarketLimitButtons from "./components/market-limit-buttons";
import ConfirmButton from "./components/confirm-button";
import { useSellBotActions } from "./hooks/use-sell-bot-hook";
import { useToken } from "../../hooks/use-token";
import { useBuySellBotTokenContext } from "../../providers/buy-sell-token-provider";
import { useWallet, BotWalletSupportedChain } from "@/providers/wallet";
import { useBotSellTokenMutation } from "@/api/bot/bot-sell-token";
import { useBotLimitSellMutation } from "@/api/bot/bot-limit-sell";
import { useWeb3Auth } from "@web3auth/modal-react-hooks";
import { useQueryClient } from "@tanstack/react-query";
import { formatNativeCurrency, formatTokenCurrency } from "@/utils/currency";
import { BOT_GET_ORDERS_QUERY_KEY } from "@/api/bot/bot-get-orders";
import { BOT_GET_TOKEN_INFO_QUERY_KEY } from "@/api/bot/bot-get-token-info";
import { BOT_GET_USER_INFO_QUERY_KEY } from "@/api/bot/bot-get-user-info";
import { POSITIONS_QUERY_KEY } from "@/api/positions/positions-bot";
import { SCREENER_QUERY_KEY } from "@/api/token-screener/screener-tokens";
import SlippageToggleInputV2 from "./slippage-toggle-input-v2";
import LimitInputV2 from "./components/limit-input-v2";
import BalanceTokenInputV2 from "./components/balance-token-input-v2";
import BuySellOptions from "./components/buy-sell-options";
import YouReceiveTokenUsdInputV2 from "./components/you-receive-token-usd-input-v2";
import { TRANSACTIONS_BOT_QUERY_KEY } from "@/api/transactions/transactions-bot";

interface SellBotActionsV2Props {
  onSuccess?: () => void;
}

const SellBotActionsV2 = ({ onSuccess }: SellBotActionsV2Props) => {
  const token = useToken();
  const sell = useSellBotActions();
  const buySellContext = useBuySellBotTokenContext();
  const { userId, appPubKey, currentWallet: wallet } = useWallet();

  const {
    mutate: sellMutate,
    isPending: isSellPending,
    isSuccess: isSellSuccess,
  } = useBotLimitSellMutation();
  const {
    mutate: sellMutateInstant,
    isPending: isSellPendingInstant,
    isSuccess: isSellSuccessInstant,
  } = useBotSellTokenMutation();
  const { isConnected } = useWeb3Auth();
  const queryClient = useQueryClient();
  const [showSellSuccess, setShowSellSuccess] = useState(false);

  useEffect(() => {
    if (isSellSuccess || isSellSuccessInstant) {
      setShowSellSuccess(true);
      setTimeout(() => setShowSellSuccess(false), 3000);
      onSuccess?.();
    }
  }, [isSellSuccess, isSellSuccessInstant, onSuccess]);

  const handleSell = () => {
    const botChainId = token?.tokenId?.startsWith("0x")
      ? BotWalletSupportedChain.ETH
      : BotWalletSupportedChain.SOL;
    const botChainSymbol =
      botChainId === BotWalletSupportedChain.ETH ? "ETH" : "SOL";
    if (sell.state.isInstant) {
      sellMutateInstant(
        {
          params: {
            userId,
            appPubKey,
            chainId: botChainId,
            tokenAddress: token?.address ?? "",
            amount: token
              ? formatTokenCurrency(sell.state.youSell, token.decimals)
              : formatNativeCurrency(sell.state.youSell, wallet.botChainId),
            slippage: buySellContext.isSellSlippageAuto
              ? undefined
              : Number(buySellContext.sellSlippage),
            walletAddress: wallet.address,
          },
          notifyParams: {
            youSell: sell.state.youSell,
            tokenSymbol: token?.symbol,
            youReceive: sell.state.youReceive,
            chainSymbol: botChainSymbol,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: [BOT_GET_TOKEN_INFO_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [BOT_GET_USER_INFO_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [POSITIONS_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [SCREENER_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [BOT_GET_ORDERS_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [TRANSACTIONS_BOT_QUERY_KEY],
              exact: false,
            });
          },
        },
      );
    } else {
      sellMutate(
        {
          params: {
            userId,
            appPubKey,
            chainId: botChainId,
            tokenAddress: token?.address ?? "",
            amount: token
              ? formatTokenCurrency(sell.state.youSell, token.decimals)
              : formatNativeCurrency(sell.state.youSell, wallet.botChainId),
            slippage: buySellContext.isSellSlippageAuto
              ? undefined
              : Number(buySellContext.sellSlippage),
            walletAddress: wallet.address,
            triggerPrice: sell.state.triggerPrice,
          },
          notifyParams: {
            youSell: sell.state.youSell,
            tokenSymbol: token?.symbol,
            youReceive: sell.state.youReceive,
            chainSymbol: botChainSymbol,
            limitPrice: sell.state.triggerPrice,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: [BOT_GET_ORDERS_QUERY_KEY],
            });
            queryClient.invalidateQueries({
              queryKey: [BOT_GET_TOKEN_INFO_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [BOT_GET_USER_INFO_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [POSITIONS_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [SCREENER_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [TRANSACTIONS_BOT_QUERY_KEY],
              exact: false,
            });
          },
        },
      );
    }
  };

  return (
    <div className="flex flex-col mt-[0.938rem]">
      <div className="flex flex-col gap-y-[0.3125rem]">
        <MarketLimitButtons
          isInstant={sell.state.isInstant}
          onMarketClick={() => sell.handlers.handleInstantChange(true)}
          onLimitClick={() => sell.handlers.handleInstantChange(false)}
          isSell
        />
        <BalanceTokenInputV2
          youSell={sell.state.youSell}
          youSellPercentage={sell.state.youSellPercentage}
          onYouSellChange={sell.handlers.handleYouSellChange}
          onYouSellPercentageChange={
            sell.handlers.handleYouSellPercentageChange
          }
        />
        <BuySellOptions
          value={sell.state.youSellPercentage}
          onOptionSelect={(v) => sell.handlers.handleYouSellPercentageChange(v)}
          isNativeToken={false}
        />
        <YouReceiveTokenUsdInputV2
          youReceive={sell.state.youReceive}
          youReceiveUSD={sell.state.youReceiveUSD}
          onYouReceiveChange={sell.handlers.handleYouReceiveChange}
          onYouReceiveUSDChange={sell.handlers.handleYouReceiveUSDChange}
        />
      </div>
      {!sell.state.isInstant && (
        <LimitInputV2
          triggerPrice={sell.state.triggerPrice}
          limit={sell.state.limit}
          onLimitChange={sell.handlers.handleLimitChange}
        />
      )}

      <SlippageToggleInputV2
        isAuto={buySellContext.isBuySlippageAuto}
        value={buySellContext.buySlippage}
        onAutoChange={() =>
          buySellContext.setIsBuySlippageAuto((prev) => !prev)
        }
        onValueChange={(v) => buySellContext.setBuySlippage(v)}
      />
      <ConfirmButton
        enabled={
          isConnected &&
          !isSellPending &&
          !isSellPendingInstant &&
          !showSellSuccess
        }
        onClick={handleSell}
        loading={isSellPending || isSellPendingInstant}
        success={showSellSuccess}
        className="mt-[1.25rem] w-full h-[1.875rem] hover:bg-red75Bg"
        background="red2Bg"
        gradient="red2"
        activeStyle="red2"
        hover="none"
      />
    </div>
  );
};

export default SellBotActionsV2;
